import { defineComponent, ref, watch, onBeforeUnmount } from 'vue-demi';
import './OrButton-78a118d7.js';
import { s as script$1 } from './OrIconButton-ad0cbc8d.js';
import { s as script$2 } from './OrOverlay-451dce49.js';
import { onClickOutside } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { resolveComponent, openBlock, createElementBlock, createVNode, withCtx, createElementVNode, normalizeClass, renderSlot, toDisplayString, withDirectives, vShow, createCommentVNode } from 'vue';
import { s as styleInject } from './style-inject.es-4c6f2515.js';

var OrModalSizes;
(function (OrModalSizes) {
  OrModalSizes["tiny"] = "tiny";
  OrModalSizes["small"] = "small";
  OrModalSizes["medium"] = "medium";
  OrModalSizes["large"] = "large";
  OrModalSizes["fullScreen"] = "fullScreen";
})(OrModalSizes || (OrModalSizes = {}));

var script = defineComponent({
  name: 'OrModal',
  components: {
    OrIconButton: script$1,
    OrOverlay: script$2
  },
  props: {
    titleText: {
      type: String,
      required: false,
      default: null
    },
    showClose: {
      type: Boolean,
      default: true
    },
    closeOnClickOutside: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      required: false,
      default: OrModalSizes.small
    },
    isOpen: {
      type: Boolean,
      required: true
    },
    isWithoutOverlay: {
      type: Boolean,
      required: false,
      default: false
    },
    trapFocus: {
      type: Boolean,
      default: true
    }
  },
  emits: ['close'],
  setup(props, _ref) {
    let {
      emit
    } = _ref;
    const overlay = ref();
    let container = ref(null);
    let trap = useFocusTrap(container, {
      immediate: props.trapFocus,
      fallbackFocus: '.container',
      allowOutsideClick: true
    });
    const handleClose = () => emit('close');
    onClickOutside(container, evt => {
      if (!evt.composedPath().includes(overlay.value.$el)) return;
      if (props.closeOnClickOutside) handleClose();
    });
    const toggleFocusTrap = isNeedToTrap => {
      if (isNeedToTrap && props.isOpen) {
        trap.activate();
      } else {
        trap === null || trap === void 0 ? void 0 : trap.deactivate();
      }
    };
    watch(() => props.trapFocus, toggleFocusTrap, {
      immediate: true
    });
    watch(() => props.isOpen, isOpen => {
      toggleFocusTrap(false);
      if (isOpen) {
        container = ref(null);
      } else {
        trap === null || trap === void 0 ? void 0 : trap.deactivate();
      }
    });
    onBeforeUnmount(() => {
      trap === null || trap === void 0 ? void 0 : trap.deactivate();
    });
    return {
      container,
      overlay,
      handleClose
    };
  },
  computed: {
    sizeClass() {
      return `is-${this.size}`;
    }
  }
});

const _hoisted_1 = {
  key: 0,
  class: "or-modal"
};
const _hoisted_2 = {
  class: "header"
};
const _hoisted_3 = {
  class: "title"
};
const _hoisted_4 = {
  class: "content"
};
const _hoisted_5 = {
  class: "footer"
};
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_or_icon_button = resolveComponent("or-icon-button");
  const _component_or_overlay = resolveComponent("or-overlay");
  return _ctx.isOpen ? (openBlock(), createElementBlock("div", _hoisted_1, [createVNode(_component_or_overlay, {
    ref: "overlay",
    "is-active": "",
    "is-without-background": _ctx.isWithoutOverlay
  }, {
    default: withCtx(() => [createElementVNode("div", {
      ref: "container",
      class: normalizeClass([_ctx.sizeClass, 'container', {
        'is-without-overlay': _ctx.isWithoutOverlay
      }])
    }, [renderSlot(_ctx.$slots, "header", {}, () => [createElementVNode("div", _hoisted_2, [renderSlot(_ctx.$slots, "title", {}, () => [createElementVNode("div", _hoisted_3, toDisplayString(_ctx.titleText), 1 /* TEXT */)]), withDirectives(createVNode(_component_or_icon_button, {
      icon: "close",
      "icon-color": "neutral-6",
      onClick: _ctx.handleClose
    }, null, 8 /* PROPS */, ["onClick"]), [[vShow, _ctx.showClose]])])]), renderSlot(_ctx.$slots, "content", {}, () => [createElementVNode("div", _hoisted_4, [renderSlot(_ctx.$slots, "default")])]), withDirectives(createElementVNode("div", _hoisted_5, [renderSlot(_ctx.$slots, "footer", {
      close: _ctx.handleClose
    })], 512 /* NEED_PATCH */), [[vShow, _ctx.$slots.footer]])], 2 /* CLASS */)]),

    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["is-without-background"])])) : createCommentVNode("v-if", true);
}

var css_248z = "/**\n * A simple way to manage mobile breakpoints described in https://davidwalsh.name/write-media-queries-sass.\n * Can be further extended using following approach https://rimdev.io/making-media-query-mixins-with-sass/\n */\n@keyframes spinAround-083760a4 {\nfrom {\n    transform: rotate(0);\n}\nto {\n    transform: rotate(359deg);\n}\n}\n@keyframes fade-083760a4 {\nfrom {\n    opacity: 1;\n}\nto {\n    opacity: 0;\n}\n}\n.or-modal[data-v-083760a4] {\n  position: fixed;\n  top: 0;\n  left: 0;\n  z-index: 50;\n  width: 100%;\n  height: 100%;\n}\n.or-modal .or-overlay[data-v-083760a4] {\n  align-items: center;\n  justify-content: center;\n}\n.or-modal .container[data-v-083760a4] {\n  position: absolute;\n  display: flex;\n  flex-flow: column;\n  max-width: 100%;\n  max-height: 80%;\n  background-color: var(--c-neutral-0);\n  border-radius: var(--s-2);\n  outline: none;\n}\n.or-modal .container.is-without-overlay[data-v-083760a4] {\n  box-shadow: 0 0.125rem 0.5rem 0 rgba(0, 0, 0, 0.1);\n}\n.or-modal .container.is-fullScreen[data-v-083760a4] {\n  width: calc(100% - var(--s-7));\n  height: calc(100% - var(--s-7));\n  max-height: calc(100% - var(--s-7));\n  margin: 0 auto;\n}\n.or-modal .container.is-tiny[data-v-083760a4] {\n  width: 320px;\n}\n.or-modal .container.is-small[data-v-083760a4] {\n  width: 480px;\n}\n.or-modal .container.is-medium[data-v-083760a4] {\n  width: 700px;\n}\n.or-modal .container.is-large[data-v-083760a4] {\n  width: 1049px;\n}\n.or-modal .container .header[data-v-083760a4] {\n  display: flex;\n  justify-content: space-between;\n  padding: 24px 24px 20px 24px;\n  border-radius: var(--s-2) var(--s-2) 0 0;\n}\n.or-modal .container .header .title[data-v-083760a4] {\n  font-size: 1.125rem;\n  font-weight: bold;\n}\n.or-modal .container .content[data-v-083760a4] {\n  flex-grow: 1;\n  padding: 0 24px 24px 24px;\n  overflow: auto;\n}\n.or-modal .container .footer[data-v-083760a4] {\n  padding: 16px 24px 24px 24px;\n  border-radius: 0 0 var(--s-2) var(--s-2);\n  box-shadow: 0 -3px 4px 0 rgba(0, 0, 0, 0.08);\n}";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-083760a4";
script.__file = "src/components/or-modal/OrModal.vue";

export { OrModalSizes as O, script as s };
